import React from "react"
import gql from "graphql-tag"
import { graphql } from "@apollo/client/react/hoc"
import Loader from "./Loader"
import withApolloClient from "../withApolloClient"

const CurrentUserQuery = gql`query CurrentUserQuery {
    currentUser {
        id
        member {
            id
            displayFullUsername
            branches {
                edges {
                    node {
                        id
                        name
                    }
                }
            }
        }
    }
}`

const memberTablePreference = gql`
  query memberTablePreference($memberId: ID!, $tableId: String!) {
    memberTablePreference(memberId: $memberId, tableId: $tableId)
  }
`

const ManagePreferenceHOC = (tableId, tableHeaders = []) => (WrappedComponent) => {

    if (typeof WrappedComponent !== "function" && typeof WrappedComponent !== "object") {
        return () => null
    }

    class GraphQLContainer extends React.Component {
        constructor(props) {
            super(props)
            this.state = {
                columnConfig: tableHeaders || [],
            }
        }

        getResolvedTableId = (props = this.props) =>
            typeof tableId === "function" ? tableId(props) : tableId

        resetToDefaultColumns = () => {
            const fixedColumns = (tableHeaders || [])
                .filter(h => h.positionIsFixed)
                .map((h, i) => ({ ...h, visible: true, order: i }))

            const allVisible = (tableHeaders || [])
                .filter(h => !h.positionIsFixed)
                .map((h, i) => ({ ...h, visible: true, order: fixedColumns.length + i }))

            this.setState({ columnConfig: [...fixedColumns, ...allVisible] })
        }

        componentDidUpdate(prevProps) {
            const { loading, memberLoading, memberTablePreference } = this.props
            const previousTableId = this.getResolvedTableId(prevProps)
            const currentTableId = this.getResolvedTableId(this.props)

            if (previousTableId !== currentTableId) {
                this.resetToDefaultColumns()
            }

            if (loading || memberLoading) return
            if (Array.isArray(memberTablePreference) && prevProps.memberTablePreference !== memberTablePreference) {
                this.processPreferences(memberTablePreference)
            }
        }

        processPreferences = (preferenceData) => {
            const visibleIds = new Set(preferenceData)
            const knownMap = new Map((tableHeaders || []).map(h => [h.id, h]))

            // Fixed columns always come first, regardless of saved order
            const fixedColumns = (tableHeaders || [])
                .filter(h => h.positionIsFixed)
                .map((h, i) => ({ ...h, visible: true, order: i }))
            const fixedIds = new Set(fixedColumns.map(h => h.id))

            // No saved preference — show all non-fixed columns as visible in default order
            if (!Array.isArray(preferenceData) || preferenceData.length === 0) {
                const allVisible = (tableHeaders || [])
                    .filter(h => !h.positionIsFixed)
                    .map((h, i) => ({ ...h, visible: true, order: fixedColumns.length + i }))
                this.setState({ columnConfig: [...fixedColumns, ...allVisible] })
                return
            }

            // Build the ordered list by walking preferenceData in saved order (skip fixed)
            const orderedVisible = preferenceData
                .filter(id => !fixedIds.has(id))
                .map((id, index) => {
                    if (knownMap.has(id)) {
                        return { ...knownMap.get(id), visible: true, order: fixedColumns.length + index }
                    }
                    return { id, name: id, visible: true, showInPicker: true, positionIsFixed: false, order: fixedColumns.length + index, isCustom: true }
                })

            // Append standard columns not in saved preferences (hidden), preserving relative order
            const hiddenStandard = (tableHeaders || [])
                .filter(h => !h.positionIsFixed && !visibleIds.has(h.id))
                .map((h, i) => ({ ...h, visible: false, order: fixedColumns.length + preferenceData.length + i }))

            const merged = [...fixedColumns, ...orderedVisible, ...hiddenStandard]

            const prev = this.state.columnConfig
            const changed = prev.length !== merged.length || merged.some((c, i) => {
                const p = prev[i]
                return !p || p.id !== c.id || p.visible !== c.visible || p.order !== c.order
            })

            if (changed) {
                this.setState({ columnConfig: merged })
            }
        }

        refetchPreference = () => {
            if (typeof this.props.refetch !== "function") return
            this.props.refetch().then((response) => {
                const prefs = response?.data?.memberTablePreference
                if (Array.isArray(prefs)) {
                    this.processPreferences(prefs)
                }
            }).catch((error) => {
                console.error("Error refetching preferences:", error)
            })
        }

        render() {
            const { loading, currentUser, memberLoading, memberTablePreference, ...rest } = this.props
            const { columnConfig } = this.state

            if ((loading && !currentUser) || !currentUser) return <Loader />

            return (
                <WrappedComponent {...rest} columnConfig={columnConfig} memberTablePreference={memberTablePreference} refetchPreferences={this.refetchPreference} currentUser={currentUser} />
            )
        }
    }

    return graphql(CurrentUserQuery, {
        props: ({ data: { currentUser, loading } }) => ({
            currentUser,
            loading,
        }),
    })(
        graphql(memberTablePreference, {
            options: (props) => {
                const memberId = props?.currentUser?.member?.id
                return {
                    variables: {
                        memberId,
                        tableId: typeof tableId === "function" ? tableId(props) : tableId,
                    },
                    fetchPolicy: "cache-and-network",
                    notifyOnNetworkStatusChange: true,
                }
            },
            skip: (props) => {
                const memberId = props?.currentUser?.member?.id
                return !memberId
            },
            props: ({ data: { memberTablePreference, loading, error, refetch } }) => ({
                memberTablePreference,
                memberLoading: loading,
                error,
                refetch,
            }),
        })(withApolloClient(GraphQLContainer))
    )
}

export default ManagePreferenceHOC
